import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { addChild } from '../api/use-people.js';
import { ApiError } from '../api/client.js';
import { useMeContext } from '../api/me-provider.js';
import { useWaivers } from '../api/use-waivers.js';
import { fillWaiver } from '../api/fill-waiver.js';
import { ConsentBox } from '../components/consent-box.js';
import { WaitNote } from '../components/wait-note.js';
import { BusyLabel } from '../components/busy-label.js';

/**
 * Add a child — names, a date of birth, and the two consents.
 *
 * THE CONSENTS (ruled 13 October, overturning "and nothing else"): the camp
 * form's two boxes, its text verbatim, the guardian's name from the account and
 * the child's filled in as it is typed. Participation, liability and medical is
 * required; photo and video is optional. What is sent with each tick is the
 * words on the screen, and the API refuses words that differ from its own.
 *
 * No country: that is a fact about the account, not about a person on it. No
 * sport: there is no sport table, so the field would have nothing behind it. No
 * "will they take lessons": athlete-ness is emergent — somebody who takes a
 * lesson is an athlete by having done it — which is the same reasoning profile
 * completion records for the account holder.
 *
 * One CTA. The API's 422 names the field as well as the fault, so the message
 * lands under the control it is about rather than at the top of the form where
 * the reader has to work out which input it means.
 */

const FIELD_MESSAGES: Record<string, string> = {
  required: 'This is needed.',
  too_long: 'That is too long.',
  invalid_date: 'That is not a date.',
  in_the_future: 'That date has not happened yet.',
  not_agreed: 'Approve before adding.',
};

export function AddChildRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [participation, setParticipation] = useState(false);
  const [media, setMedia] = useState(false);
  const [saving, setSaving] = useState(false);
  const { state: meState } = useMeContext();
  const { state: waiverState, reload: reloadWaivers } = useWaivers();

  const profile = meState.status === 'ready' ? meState.me.profile : undefined;
  const guardian = profile ? `${profile.first_name} ${profile.last_name}` : '';
  // Trimmed, as the API trims them: the words compared are the words stored.
  const athlete = `${firstName.trim()} ${lastName.trim()}`.trim();
  const waivers = waiverState.status === 'ready' ? waiverState.waivers : null;
  const [fieldError, setFieldError] = useState<{ field: string; code: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    setFieldError(null);
    setFormError(null);

    // Checked here as well as by the API, so the message is under the box at
    // once rather than after a round trip.
    if (!participation) {
      setFieldError({ field: 'participation', code: 'not_agreed' });
      setSaving(false);
      return;
    }

    const words = (template: string) =>
      fillWaiver(template, guardian, `${firstName.trim()} ${lastName.trim()}`);

    try {
      await addChild(getToken, {
        firstName,
        lastName,
        dateOfBirth,
        consents: {
          participation: waivers?.participation
            ? { waiverId: waivers.participation.waiver_id, text: words(waivers.participation.template) }
            : null,
          media:
            media && waivers?.media
              ? { waiverId: waivers.media.waiver_id, text: words(waivers.media.template) }
              : null,
        },
      });
      // Back to the list, which fetches again on mount and so shows the person
      // that was just added without anything having to tell it to.
      navigate('/account');
    } catch (cause) {
      if (cause instanceof ApiError && cause.field) {
        setFieldError({ field: cause.field, code: cause.code });
      } else if (cause instanceof ApiError && cause.code === 'waiver_changed') {
        // The text changed while the form was open. Show the new words and ask
        // again, rather than signing the old ones.
        reloadWaivers();
        setParticipation(false);
        setMedia(false);
        setFormError('The wording has changed. Read it again and approve.');
      } else if (cause instanceof ApiError && cause.code === 'person_already_on_account') {
        setFormError('Somebody with that name and date of birth is already on your account.');
      } else {
        setFormError('That did not save. Try again.');
      }
      setSaving(false);
    }
  }

  const errorFor = (field: string) =>
    fieldError?.field === field ? (FIELD_MESSAGES[fieldError.code] ?? 'That is not right.') : null;

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/account" aria-label="Back to your account">
          ‹
        </Link>
        <h2>Add a child</h2>
      </div>

      <form className="panel" onSubmit={submit} noValidate>
        <Field
          id="first-name"
          label="First name"
          value={firstName}
          onChange={setFirstName}
          error={errorFor('firstName')}
          autoComplete="section-athlete given-name"
        />
        <Field
          id="last-name"
          label="Last name"
          value={lastName}
          onChange={setLastName}
          error={errorFor('lastName')}
          autoComplete="section-athlete family-name"
        />
        <Field
          id="date-of-birth"
          label="Date of birth"
          type="date"
          value={dateOfBirth}
          onChange={setDateOfBirth}
          error={errorFor('dateOfBirth')}
        />

        <span className="section-label">Consent</span>
        {waiverState.status === 'loading' ? <WaitNote className="empty">Checking…</WaitNote> : null}
        {waiverState.status === 'failed' ? <p className="empty">{waiverState.message}</p> : null}
        {waivers && !waivers.participation ? (
          <p className="empty">There is no waiver to sign yet, so a child cannot be added.</p>
        ) : null}
        {waivers?.participation ? (
          <ConsentBox
            id="participation"
            waiver={waivers.participation}
            guardian={guardian}
            athlete={athlete || 'your child'}
            checked={participation}
            onChange={(next) => {
              setParticipation(next);
              if (next && fieldError?.field === 'participation') setFieldError(null);
            }}
            error={errorFor('participation')}
            required
          />
        ) : null}
        {waivers?.media ? (
          <ConsentBox
            id="media"
            waiver={waivers.media}
            guardian={guardian}
            athlete={athlete || 'your child'}
            checked={media}
            onChange={setMedia}
          />
        ) : null}

        {formError ? <p className="state-note">{formError}</p> : null}

        <button className="primary" type="submit" disabled={saving || !waivers?.participation} aria-busy={saving}>
          <BusyLabel busy={saving} working="Adding…">
            Add
          </BusyLabel>
        </button>
      </form>
    </>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  error: string | null;
  type?: string;
  /** The child's name, suggested apart from the parent's own (ruled 22 October: suggestions on in the parent's forms). */
  autoComplete?: string;
}) {
  return (
    <label className="field" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        // The message is tied to the input rather than floating near it, so a
        // screen reader hears which control is wrong.
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-error" id={`${id}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
